import { motion } from "framer-motion";
import { IDS } from "@/constants/testIds";
import { OWNER } from "@/data/content";
import { ConnectDialog } from "@/components/ConnectDialog";
import { ArrowRight, ArrowUpRight } from "lucide-react";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid={IDS.hero.root}
      className="relative min-h-screen w-full pt-32 pb-16 border-b border-border grid-lines-bg"
    >
      {/* Floating blobs */}
      <div className="pointer-events-none absolute -top-10 right-4 md:right-24 h-40 w-40 md:h-64 md:w-64 rounded-full opacity-70 blur-3xl bg-[#E3FF00]/30"></div>
      <div className="pointer-events-none absolute bottom-10 left-10 h-40 w-40 rounded-full opacity-40 blur-3xl bg-[#FF3B30]/30"></div>

      <div className="mx-auto max-w-7xl px-4 sm:px-8 relative">
        {/* Meta line */}
        <div className="flex items-center gap-3 font-jet text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-8">
          <span className="inline-block h-2 w-2 bg-[#E3FF00] blink"></span>
          <span>Available for Freelance, part-time and full-time · {OWNER.location}</span>
        </div>

        {/* Massive display headline */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="font-mono-brut font-black uppercase leading-[0.9] tracking-tighter text-6xl sm:text-8xl lg:text-[10rem]"
        >
          BILAL
          <br />
          <span className="text-[#E3FF00] glow-yellow">BUILDS</span>
          <br />
          PRODUCTS.
        </motion.h1>

        {/* Sub grid: bio + stats */}
        <div className="grid grid-cols-1 md:grid-cols-12 mt-12 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="md:col-span-6 lg:col-span-5"
          >
            <p className="text-base sm:text-lg text-foreground/80 leading-relaxed font-sans-brut">
              Senior Product Engineer with <span className="text-[#E3FF00] font-semibold">5+ years</span> of
              turning founder scribbles into revenue-generating systems.
              Bubble master, React contributor, backend architect.
            </p>

            <div className="flex flex-wrap items-center gap-3 mt-8">
              <ConnectDialog
                triggerTestId={IDS.hero.ctaConnect}
                triggerLabel={<><span>Start a project</span><ArrowRight size={16} /></>}
                triggerClassName="inline-flex items-center gap-2 bg-[#E3FF00] text-[#0A0A0A] px-6 py-3 font-mono-brut font-bold uppercase text-sm tracking-widest brut-shadow-sm hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[6px_6px_0_0_#F4F4F0] transition-all"
                source="hero"
              />
              <a
                data-testid={IDS.hero.ctaProjects}
                href="#projects"
                className="inline-flex items-center gap-2 border border-[#F4F4F0] px-6 py-3 font-mono-brut font-bold uppercase text-sm tracking-widest hover:bg-[#F4F4F0] hover:text-[#0A0A0A] transition"
              >
                See projects <ArrowUpRight size={16} />
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="md:col-span-6 lg:col-span-7 md:col-start-7 lg:col-start-8"
          >
            <div className="grid grid-cols-3 border border-border">
              {[
                { k: "5+", v: "Years shipping" },
                { k: "20+", v: "Products live" },
                { k: "10K+", v: "Users scaled" },
              ].map((s, i) => (
                <div key={i} className={`p-4 sm:p-6 ${i < 2 ? "border-r border-border" : ""}`}>
                  <div className="font-mono-brut text-3xl sm:text-5xl font-black">{s.k}</div>
                  <div className="font-jet text-[10px] uppercase tracking-widest text-muted-foreground mt-2">{s.v}</div>
                </div>
              ))}
            </div>

            <div className="mt-4 p-4 border border-border bg-[#141414]">
              <div className="font-jet text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Current stack</div>
              <div className="font-mono-brut text-sm">{OWNER.stack}</div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
